import html2canvas from 'html2canvas'
import { Device, MeterBillingType, MeterCategory, ThirdPartyVendor } from '../repositories/devices-repository'
import jsPDF from 'jspdf'
import { AxiosError } from 'axios'
import { store } from '@/store'
import { UserActionStatusData, appStateActions } from '@/store/app-reducer'
import { toCanvas } from 'html-to-image'
import { eventPublisher } from '@/utils/event-publisher'

export const GHANA_POST_GPS_REGEX = /^[A-Z][A-Z0-9]-\d{3}-\d{4}$/

export function isthirdPartyVendorMeter(meter: ThirdPartyVendor | Device): meter is ThirdPartyVendor {
    return (<ThirdPartyVendor>meter).vendorId !== undefined
}

export function getMeterCategory(meter: Device) {
    if (isthirdPartyVendorMeter(meter)) return MeterCategory.THIRD_PARTY
    return meter.meterCategory?.toLowerCase()
}

export function isSLTMeter(meter: Device) {
    return getMeterCategory(meter) === MeterCategory.SLT
}

export function isPostpaidOrSLTMeter(meter: Device) {
    return getMeterCategory(meter) === MeterCategory.POSTPAID || isSLTMeter(meter)
}

export function isPostpaidOrSLTMeterOrFlatRate(meter: Device) {
    return getMeterCategory(meter) === MeterCategory.POSTPAID || isSLTMeter(meter) || isFlatRateMeter(meter)
}

export function isPrepaidMeter(meter: Device) {
    return getMeterCategory(meter) === MeterCategory.PREPAID && !isFlatRateMeter(meter)
}

export function isFlatRateMeter(meter: Device) {
    return meter.billingType === MeterBillingType.FLATRATE
}

// format credit card number

export function formatCreditCardNumber(value: string) {
    if (!value) return value

    const input = value.replace(/\D/g, '').substring(0, 16)
    const cardNumber = input.match(/.{1,4}/g)?.join(' ')

    return cardNumber ?? ''
}

// unformat credit card number
export function unformatCreditCardNumber(value: string) {
    if (!value) return ''

    const cardNumber = value.replace(/\s/g, '')

    return cardNumber || ''
}

// fomart credit card expiry date

export function formatCreditCardExpiryDate(value: string) {
    if (!value) return ''

    let input = value.replace(/\D/g, '').substring(0, 4)
    if (input.length > 2 || input.length == 2) {
        input = input.substring(0, 2) + '/' + input.substring(2)
    }
    return input || ''
}

export function formatGhanaCardNumber(input: string) {
    const pattern = /^([A-Z]{3})?(-)?(\d{9})?(-)?(\d)?$/
    // Apply the desired pattern
    let formattedValue = input.replace(pattern, function (match, p1, p2, p3, p4, p5) {
        // Construct the formatted string
        let result = ''
        if (p1) {
            result += p1
        }
        if (p3) {
            result += '-' + p3
        }
        if (p5) {
            result += '-' + p5
        }
        return result
    })

    // Update the input value
    return formattedValue.toUpperCase()
}

export function validateGhanaCardNumber(input: string) {
    const pattern = /^([A-Z]{3})(-)(\d{9})(-)(\d+)$/
    const isValid = pattern.test(input)
    return isValid
}

export function formatGhanaPostAddress(input: string) {
    // Update the input value
    return input.toUpperCase()
}

export function validateGhanaPostAddress(input?: string) {
    const pattern = /^([A-Z0-9]+)(-)(\d+)(-)(\d+)$/
    const isValid = pattern.test(input ?? '')
    return isValid
}

export function countWords(str: string) {
    str = str.trim()
    if (str === '') return 0
    const words = str.split(/\s+/)
    return words.length
}

export function stringToTitleCase(text: string) {
    return text?.replace(/\w\S*/g, function (txt) {
        return txt?.charAt(0).toUpperCase() + txt.substring(1).toLowerCase()
    })
}

export function stringToPascalCase(text: string) {
    return stringToTitleCase(text).trim().replace(/\s+/g, '')
}

export const EVENT_TYPES = {
    newServiceApplicationReceived: {
        type: 'new-service-application-received',
    },
    ApplicationFeeGenerated: {
        type: 'ApplicationFeeGenerated',
    },
    ApplicationFeePaid: {
        type: 'ApplicationFeePaid',
    },
    ConnectionFeeGenerated: {
        type: 'ConnectionFeeGenerated',
    },
    ConnectionFeePaid: {
        type: 'ConnectionFeePaid',
    },
    InspectionInitiated: {
        type: 'InspectionInitiated',
    },
    InspectionFailed: {
        type: 'InspectionFailed',
    },
    InspectionSuccessful: {
        type: 'InspectionSuccessful',
    },
    ContractGenerated: {
        type: 'ContractGenerated',
    },
    ContractSigned: {
        type: 'ContractSigned',
    },
    InstallationInitiated: {
        type: 'InstallationInitiated',
    },
    InstallationAssigned: {
        type: 'InstallationAssigned',
    },
    InstallationSuccessful: {
        type: 'InstallationSuccessful',
    },
    InstallationFailed: {
        type: 'InstallationFailed',
    },
    ServiceRequestCompleted: {
        type: 'ServiceRequestCompleted',
    },
    CustomerTariffClassUpdated: {
        type: 'CustomerTariffClassUpdated',
    },
    CustomerAccountDetailChangeApproved: {
        type: 'CustomerAccountDetailChangeApproved',
    },
    CustomerAccountDetailChangeRejected: {
        type: 'CustomerAccountDetailChangeRejected',
    },
    CustomerUpdated: {
        type: 'CustomerUpdated',
    },
    NewMeterInstalled: {
        type: 'NewMeterInstalled',
    },
    MeterReading: {
        type: 'MeterReading',
    },
    MeterReplaced: {
        type: 'MeterReplaced',
    },
    MeterReadingException: {
        type: 'MeterReadingException',
    },
    MeterReadingAnomaly: {
        type: 'MeterReadingAnomaly',
    },
    RegularMonthReading: {
        type: 'RegularMonthReading',
    },
    RecoveryBillCancellation: {
        type: 'RecoveryBillCancellation',
    },
    BillRegularization: {
        type: 'BillRegularization',
    },
}

export enum SERVICE_REQUEST_STAGES {
    InspectionStatus = 'Inspection Status',
    ContractStatus = 'Contract Status',
    InstallationStatus = 'Installation Status',
    InstallationStatusAssinged = 'Installation Status Assigned',
}

export function getStreamStatusClass(status: keyof typeof EVENT_TYPES, serviceRequestStage: SERVICE_REQUEST_STAGES) {
    const statusLower = status.toLowerCase()
    const SUCCESS_ARRAY = [
        statusLower === EVENT_TYPES.ContractGenerated.type.toLowerCase() &&
            serviceRequestStage === SERVICE_REQUEST_STAGES.InspectionStatus,
        statusLower === EVENT_TYPES.ContractSigned.type.toLowerCase() &&
            serviceRequestStage === SERVICE_REQUEST_STAGES.InspectionStatus,
        statusLower === EVENT_TYPES.ContractSigned.type.toLowerCase() &&
            serviceRequestStage === SERVICE_REQUEST_STAGES.ContractStatus,
        statusLower === EVENT_TYPES.InstallationInitiated.type.toLowerCase() &&
            serviceRequestStage === SERVICE_REQUEST_STAGES.InspectionStatus,
        statusLower === EVENT_TYPES.InspectionSuccessful.type.toLowerCase() &&
            serviceRequestStage === SERVICE_REQUEST_STAGES.InstallationStatus,
        statusLower === EVENT_TYPES.InspectionSuccessful.type.toLowerCase() &&
            serviceRequestStage === SERVICE_REQUEST_STAGES.InspectionStatus,
        statusLower === EVENT_TYPES.InstallationSuccessful.type.toLowerCase() &&
            serviceRequestStage === SERVICE_REQUEST_STAGES.InspectionStatus,
        statusLower === EVENT_TYPES.InstallationInitiated.type.toLowerCase() &&
            serviceRequestStage === SERVICE_REQUEST_STAGES.ContractStatus,
        statusLower === EVENT_TYPES.InstallationSuccessful.type.toLowerCase() &&
            serviceRequestStage === SERVICE_REQUEST_STAGES.ContractStatus,
        statusLower === EVENT_TYPES.InspectionSuccessful.type.toLowerCase() &&
            serviceRequestStage === SERVICE_REQUEST_STAGES.ContractStatus,
    ]
    const FAILED_ARRAY = [
        statusLower === EVENT_TYPES.InstallationFailed.type.toLowerCase(),
        statusLower === EVENT_TYPES.InspectionFailed.type.toLowerCase(),
        statusLower === EVENT_TYPES.CustomerAccountDetailChangeRejected.type.toLowerCase(),
        statusLower === EVENT_TYPES.MeterReadingAnomaly.type.toLowerCase(),
    ]
    if (
        statusLower === EVENT_TYPES.InspectionInitiated.type.toLowerCase() &&
        serviceRequestStage === SERVICE_REQUEST_STAGES.InspectionStatus
    ) {
        return 'pending'
    } else if (SUCCESS_ARRAY.includes(true)) {
        return 'success'
    } else if (FAILED_ARRAY.includes(true)) {
        return 'failed'
    } else {
        return 'pending'
    }
}

export const onDownloadElementAsPDF = async ({
    elementToPrint,
    fileName,
}: {
    elementToPrint: string
    fileName: string
}) => {
    const receiptContainer = document.getElementById(elementToPrint)

    if (!receiptContainer) {
        return
    }

    const canvas = await html2canvas(receiptContainer, {
        windowWidth: receiptContainer.scrollWidth,
        windowHeight: receiptContainer.scrollHeight,
    })
    const data = canvas.toDataURL('image/png')

    const pdf = new jsPDF()
    const imgProperties = pdf.getImageProperties(data)
    const pdfWidth = pdf.internal.pageSize.getWidth()
    const pdfHeight = (imgProperties.height * pdfWidth) / imgProperties.width

    pdf.addImage(data, 'PNG', 0, 0, pdfWidth, pdfHeight)
    pdf.save(`${fileName}.pdf`)
}

export function getNewServiceRequestStatusClass(status: string, serviceRequestStage: SERVICE_REQUEST_STAGES) {
    const statusLower = status.toLowerCase()
    const SUCCESS_ARRAY = ['paid', 'completed', 'signed']
    const FAILED_ARRAY = ['']

    if (SUCCESS_ARRAY.includes(statusLower)) {
        return 'success'
    } else if (FAILED_ARRAY.includes(statusLower)) {
        return 'failed'
    } else {
        return 'pending'
    }
}

export function getReportStatusClass(status: string) {
    const statusLower = status.toLowerCase()
    const SUCCESS_ARRAY = [
        EVENT_TYPES.ContractGenerated.type.toLowerCase(),
        EVENT_TYPES.ContractSigned.type.toLowerCase(),
        EVENT_TYPES.ContractSigned.type.toLowerCase(),
        EVENT_TYPES.InstallationInitiated.type.toLowerCase(),
        EVENT_TYPES.InspectionSuccessful.type.toLowerCase(),
        EVENT_TYPES.InspectionSuccessful.type.toLowerCase(),
        EVENT_TYPES.InstallationSuccessful.type.toLowerCase(),
        EVENT_TYPES.InstallationInitiated.type.toLowerCase(),
        EVENT_TYPES.InstallationSuccessful.type.toLowerCase(),
        EVENT_TYPES.InspectionSuccessful.type.toLowerCase(),
        'completed',
        'refunded',
    ]
    const FAILED_ARRAY = [
        EVENT_TYPES.InstallationFailed.type.toLowerCase(),
        EVENT_TYPES.InspectionFailed.type.toLowerCase(),
        EVENT_TYPES.CustomerAccountDetailChangeRejected.type.toLowerCase(),
        EVENT_TYPES.MeterReadingAnomaly.type.toLowerCase(),
    ]

    const PENDING_ARRAY = [
        EVENT_TYPES.InspectionInitiated.type.toLowerCase(),
        'pending',
        'processing',
        'refund pending',
    ]

    if (PENDING_ARRAY.includes(statusLower)) {
        return 'pending'
    } else if (SUCCESS_ARRAY.includes(statusLower)) {
        return 'success'
    } else if (FAILED_ARRAY.includes(statusLower)) {
        return 'failed'
    } else if (statusLower === 'discarded') {
        return 'discarded'
    } else {
        return 'pending'
    }
}

export function toNumber(value: any) {
    return Number(value ?? 0)
}

export function conditionalSelector<T = any>({
    condition,
    trueBlock,
    falseBlock,
}: {
    condition: T
    trueBlock: any
    falseBlock?: any
}) {
    // Recheck this commented code; make it functional
    // const handlers = [() => falseBlock, () => trueBlock]
    // return handlers[+!!condition]() // Convert condition to 0 or 1 and select the function
    return condition ? trueBlock : falseBlock ?? null
}

export const maskString = (str: string | null) => {
    if (!str) return ''
    let toBeMasked = str.substring(3, str.length - 1)
    return str.replace(toBeMasked, '********')
}

export enum BrowserPermission {
    CAMERA = 'camera',
    GEOLOCATION = 'geolocation',
}

export enum BrowserPermissionStatus {
    GRANTED = 'granted',
    DENIED = 'denied',
    PROMPT = 'prompt',
    NOT_SUPPORTED = 'not supported',
}

export async function queryBrowserPermission(permission: BrowserPermission) {
    try {
        const result = await navigator.permissions.query({ name: permission as PermissionName })
        return result.state
    } catch (error) {
        return 'not supported'
    }
}

export const setUserActionStatus = (payload: UserActionStatusData) => {
    store.dispatch(appStateActions.setUserActionStatusData(payload))
}

export function extractFilenameFromURL(url: string) {
    try {
        // Create a URL object to properly parse components of the URL string
        const urlObj = new URL(url)

        // Get the pathname (everything after the domain)
        const pathname = urlObj.pathname

        // Remove trailing slashes
        const cleanPath = pathname.replace(/\/+$/, '')

        // If path is empty or just a slash, there's no filename
        if (cleanPath === '' || cleanPath === '/') {
            return null
        }

        // Split the path and get the last segment
        const pathParts = cleanPath.split('/')
        return pathParts[pathParts.length - 1]
    } catch (e) {
        // If URL parsing fails, fall back to the original logic
        const cleanUrl = url.replace(/\/+$/, '')
        const parts = cleanUrl.split('/')

        // If it's just a domain (or less), return null
        if (parts.length <= 3) {
            // protocol, empty string, domain
            return null
        }

        return parts[parts.length - 1]
    }
}

export const getErrorMessage = (error: AxiosError) => {
    const errorMessage = (error.response?.data as any)?.message ?? error?.message
    eventPublisher.logAnalyticsEvent({
        eventType: 'Exception',
        actionName: 'API Error',
        ReasonForfailure: errorMessage,
        payload: { error: errorMessage },
    })
    return errorMessage || 'An unexpected error occurred'
}

export function extractMoneyValue(text: string): number | null {
    const regex = /GHS\s*([+-]?(?:\d+(?:\.\d{1,2})?|\.\d{1,2}))/i
    const match = regex.exec(text)

    if (match) {
        const numericPart = match[1]
        const numericValue = parseFloat(numericPart)
        return numericValue
    }

    return null
}

export async function downloadFileInNewTab(url: string) {
    const fileName = extractFilenameFromURL(url) // Extract filename from URL
    const link = document.createElement('a')
    link.href = url // Create a temporary URL for the Blob data
    link.setAttribute('download', fileName ?? `${new Date()}.pdf`) // Set the download attribute with filename
    link.setAttribute('target', '_blank')
    link.click() // Simulate a click on the link to trigger download

    document.body.removeChild(link)
}

export const onDownloadPDF = async ({
    containerElementId,
    fileName,
}: {
    containerElementId: string
    fileName: string
}) => {
    const receiptContainer = document.getElementById(containerElementId)
    if (!receiptContainer) {
        return
    }

    try {
        // Generate canvas from the element
        const canvas = await toCanvas(receiptContainer)
        const imgWidth = canvas.width
        const imgHeight = canvas.height

        const pdf = new jsPDF({
            orientation: 'p',
            unit: 'mm',
            format: 'a4',
        })

        const pageWidth = pdf.internal.pageSize.getWidth()
        const pageHeight = pdf.internal.pageSize.getHeight()
        const margins = {
            top: 10,
            bottom: 10,
            left: 10,
            right: 10,
        }

        const contentWidth = pageWidth - margins.left - margins.right
        const contentHeight = pageHeight - margins.top - margins.bottom

        // Calculate the scale to fit width
        const scale = contentWidth / imgWidth
        const scaledImgHeight = imgHeight * scale

        // If the scaled image fits on one page
        if (scaledImgHeight <= contentHeight) {
            const imgData = canvas.toDataURL('image/png')
            pdf.addImage(imgData, 'PNG', margins.left, margins.top, contentWidth, scaledImgHeight)
        } else {
            // Multi-page: slice the canvas into page-sized chunks
            let position = 0
            let pageNum = 0
            const pageCanvasHeight = Math.floor(contentHeight / scale)
            while (position < imgHeight) {
                // Create a temporary canvas for the current page
                const pageCanvas = document.createElement('canvas')
                pageCanvas.width = imgWidth
                pageCanvas.height = Math.min(pageCanvasHeight, imgHeight - position)
                const ctx = pageCanvas.getContext('2d')
                if (ctx) {
                    ctx.drawImage(canvas, 0, position, imgWidth, pageCanvas.height, 0, 0, imgWidth, pageCanvas.height)
                }
                const imgData = pageCanvas.toDataURL('image/png')
                if (pageNum > 0) pdf.addPage()
                pdf.addImage(imgData, 'PNG', margins.left, margins.top, contentWidth, pageCanvas.height * scale)
                position += pageCanvasHeight
                pageNum++
            }
        }

        pdf.save(`${fileName}.pdf`)
    } catch (error) {
        console.error('Download failed:', error)
    }
}

export enum ScrollAxis {
    BOTH = 'BOTH',
    VERTICAL = 'VERTICAL',
    HORIZONTAL = 'HORIZONTAL',
}

export function scrollElementIntoView({
    elementId,
    axis = ScrollAxis.HORIZONTAL,
}: {
    elementId: string
    axis?: ScrollAxis
}) {
    const element = document.getElementById(elementId)

    if (!element) {
        console.warn(`Element with id "${elementId}" not found`)
        return
    }

    const scrollableParent = getScrollableParent(element)
    const elementRect = element.getBoundingClientRect()
    const containerRect = scrollableParent.getBoundingClientRect()

    let scrollX = 0
    let scrollY = 0

    if (axis === ScrollAxis.VERTICAL || axis === ScrollAxis.BOTH) {
        if (elementRect.top < containerRect.top) {
            scrollY = elementRect.top - containerRect.top
        } else if (elementRect.bottom > containerRect.bottom) {
            scrollY = elementRect.bottom - containerRect.bottom
        }
    }

    if (axis === ScrollAxis.HORIZONTAL || axis === ScrollAxis.BOTH) {
        if (elementRect.left < containerRect.left) {
            scrollX = elementRect.left - containerRect.left
        } else if (elementRect.right > containerRect.right) {
            scrollX = elementRect.right - containerRect.right
        }
    }

    if (scrollX !== 0 || (scrollY !== 0 && typeof scrollableParent.scrollBy === 'function')) {
        scrollableParent.scrollBy({
            top: scrollY,
            left: scrollX,
            behavior: 'smooth',
        })
    }
}

function getScrollableParent(element: HTMLElement): HTMLElement {
    if (element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth) {
        return element
    }
    return element.parentElement ? getScrollableParent(element.parentElement) : document.body
}

export const objectHasNullProperties = (obj: any) => Object.values(obj).every(value => !value)

export function numberToWords(num: number): string {
    if (num >= 100) {
        return `${num}`
    }

    // Arrays for words representation
    const ones: string[] = [
        '',
        'one',
        'two',
        'three',
        'four',
        'five',
        'six',
        'seven',
        'eight',
        'nine',
        'ten',
        'eleven',
        'twelve',
        'thirteen',
        'fourteen',
        'fifteen',
        'sixteen',
        'seventeen',
        'eighteen',
        'nineteen',
    ]

    const tens: string[] = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety']

    // Handle numbers less than 20 directly from the ones array
    if (num < 20) {
        return ones[num]
    }

    // Handle numbers 20-99
    const tenDigit: number = Math.floor(num / 10)
    const oneDigit: number = num % 10

    // If the ones digit is 0, just return the tens word
    if (oneDigit === 0) {
        return tens[tenDigit]
    }

    // Otherwise combine with a hyphen
    return tens[tenDigit] + '-' + ones[oneDigit]
}

export function getStringOrDefault(value?: string | null, defaultValue?: string | null) {
    // First handle null/undefined with ??, then check for empty string
    return (value ?? '') === '' ? defaultValue : value!
}

/**
 * Converts a file to a base64 string
 * @param {File} file - The file to convert
 * @returns {Promise<string>} A promise that resolves to the base64 string
 */
export function fileToBase64(file: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader()

        reader.onload = (): void => {
            // The result is in the format "data:mime/type;base64,BASE64_STRING"
            // We split by the comma and take the second part to get just the base64 string
            const base64String = reader.result?.toString().split(',')[1] ?? ''
            resolve(base64String)
        }

        reader.onerror = (error: ProgressEvent<FileReader>): void => {
            reject(new Error(`Error converting file to base64`))
        }

        // Read the file as a data URL, which will be encoded as base64
        reader.readAsDataURL(file)
    })
}

export function getPreviousMonthDates(baseDate: Date | string): { startDate: string; endDate: string } {
    // Clone the base date to avoid modifying the original
    const previousMonthDate = new Date(baseDate)

    // Move to the previous month
    previousMonthDate.setMonth(previousMonthDate.getMonth() - 1)

    // Get start of the month (1st day)
    const startDate = new Date(previousMonthDate)
    startDate.setDate(1)
    startDate.setHours(0, 0, 0, 0)

    // Get end of the month (last day)
    const endDate = new Date(previousMonthDate)
    endDate.setMonth(endDate.getMonth() + 1)
    endDate.setDate(0) // Setting day 0 of next month gets the last day of current month
    endDate.setHours(23, 59, 59, 999)

    return { startDate: startDate.toISOString(), endDate: endDate.toISOString() }
}

export function convertMarkdownLinks(text?: string | null): string | null | undefined {
    if (!text || typeof text !== 'string') return text

    const linkRegex = /\[([^[\]]*)\]\(([^()]*)\)/g

    return text.replace(linkRegex, (match, linkText, url) => {
        return `<a href="${url}" target="_blank">${linkText}</a>`
    })
}

export const logAction = ({
    actionName,
    additionalContext,
}: {
    actionName: string
    additionalContext?: Record<string, any>
}) => {
    eventPublisher.trackUserAction(actionName, {
        ...additionalContext,
    })
}
